<template>
  <Teleport to="body">
    <Transition
      name="modal"
      @enter="onEnter"
      @after-enter="onAfterEnter"
      @leave="onLeave"
      @after-leave="onAfterLeave"
    >
      <div
        v-if="show"
        class="modal-overlay"
        @click="handleOverlayClick"
      >
        <div
          ref="modalRef"
          :class="modalClasses"
          role="dialog"
          aria-modal="true"
          :aria-labelledby="titleId"
          @click.stop
        >
          <!-- Header -->
          <div v-if="showHeader" class="modal-header">
            <h3 v-if="title" :id="titleId" class="modal-title">
              {{ title }}
            </h3>
            <slot name="header" />
            <button
              v-if="showCloseButton"
              class="modal-close-button"
              @click="closeModal"
              aria-label="Close modal"
            >
              <svg
                class="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>

          <!-- Body -->
          <div class="modal-body">
            <slot />
          </div>

          <!-- Footer -->
          <div v-if="showFooter" class="modal-footer">
            <slot name="footer" />
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, ref, watch, nextTick } from 'vue'

interface Props {
  show: boolean
  title?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  persistent?: boolean
  showHeader?: boolean
  showFooter?: boolean
  showCloseButton?: boolean
  closeOnEscape?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  show: false,
  size: 'md',
  persistent: false,
  showHeader: true,
  showFooter: false,
  showCloseButton: true,
  closeOnEscape: true
})

const emit = defineEmits<{
  'update:show': [value: boolean]
  close: []
  open: []
}>()

const modalRef = ref<HTMLElement>()
const titleId = computed(() => `modal-title-${Math.random().toString(36).substr(2, 9)}`)

const modalClasses = computed(() => {
  const baseClasses = [
    'modal-content',
    'bg-white',
    'rounded-lg',
    'shadow-xl',
    'transform',
    'transition-all',
    'overflow-hidden',
    'flex',
    'flex-col',
    'max-h-[90vh]'
  ]

  const sizeClasses = {
    sm: ['max-w-sm', 'w-full', 'mx-4'],
    md: ['max-w-md', 'w-full', 'mx-4'],
    lg: ['max-w-lg', 'w-full', 'mx-4'],
    xl: ['max-w-2xl', 'w-full', 'mx-4'],
    full: ['w-full', 'h-full', 'm-4', 'max-w-none', 'max-h-none']
  }

  return [...baseClasses, ...sizeClasses[props.size]]
})

const closeModal = () => {
  if (!props.persistent) {
    emit('update:show', false)
    emit('close')
  }
}

const handleOverlayClick = () => {
  if (!props.persistent) {
    closeModal()
  }
}

const handleEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && props.closeOnEscape && props.show) {
    closeModal()
  }
}

// Focus management
const onEnter = () => {
  document.addEventListener('keydown', handleEscapeKey)
  document.body.style.overflow = 'hidden'
}

const onAfterEnter = () => {
  nextTick(() => {
    modalRef.value?.focus()
  })
  emit('open')
}

const onLeave = () => {
  document.removeEventListener('keydown', handleEscapeKey)
}

const onAfterLeave = () => {
  document.body.style.overflow = ''
}

// Watch for show changes
watch(() => props.show, (newValue) => {
  if (newValue) {
    nextTick(() => {
      modalRef.value?.focus()
    })
  }
})
</script>

<style scoped>
.modal-overlay {
  @apply fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm;
}

.modal-content {
  @apply relative outline-none;
}

.modal-header {
  @apply flex items-center justify-between p-6 border-b border-gray-200 flex-shrink-0;
}

.modal-title {
  @apply text-lg font-semibold text-gray-900;
}

.modal-close-button {
  @apply p-1 text-gray-400 hover:text-gray-600 rounded-md hover:bg-gray-100 transition-colors;
}

.modal-body {
  @apply p-6 flex-1 overflow-y-auto;
}

.modal-footer {
  @apply px-6 py-4 border-t border-gray-200 flex items-center justify-end space-x-3 flex-shrink-0;
}

/* Transitions */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-active .modal-content,
.modal-leave-active .modal-content {
  transition: all 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-from .modal-content,
.modal-leave-to .modal-content {
  transform: scale(0.9) translateY(-50px);
}
</style> 